import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { ModalBody, ModalFooter } from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { useTracking } from 'shared/providers/TrackingProvider';
import React, { useEffect, useState } from 'react';
import { CareMembershipEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useManageMembershipContext } from '../context/ManageMembershipContext';
import { DESCRIPTION_MAX_CHAR_COUNT } from '../utils';

interface GetHelpStepProps {
  selectedEmail?: string;
  submitRequest: () => void;
}

const GetHelpStep = ({ selectedEmail, submitRequest }: GetHelpStepProps): JSX.Element => {
  const { trackEvent } = useTracking();
  const [inputFieldTextError, setInputFieldTextError] = useState('');
  const { submitText, setSubmitText, isSubmitting, handleBack, previousStep } = useManageMembershipContext();

  useEffect(() => {
    trackEvent(CareMembershipEvents.ManageGetHelpClick, {
      previousStep,
    });
  }, [previousStep, trackEvent]);

  const validateText = (text: string): boolean => {
    if (text.length < 1) {
      setInputFieldTextError('Please add a description before submitting');
      return false;
    } else {
      setInputFieldTextError('');
      return true;
    }
  };

  const handleSubmit = (): void => {
    if (validateText(submitText)) {
      submitRequest();
    }
  };

  return (
    <>
      <ModalBody py={5}>
        <Heading size="md" mb={4}>
          Get Help
        </Heading>
        <Textarea
          size="md"
          label="Describe the issue you're having:"
          labelStyle={{ fontWeight: 'semibold' }}
          helperText={`${submitText.length}/${DESCRIPTION_MAX_CHAR_COUNT}`}
          isInvalid={!!inputFieldTextError || submitText.length > DESCRIPTION_MAX_CHAR_COUNT}
          isRequired
          errorText={
            inputFieldTextError || `Please limit your description to ${DESCRIPTION_MAX_CHAR_COUNT} characters.`
          }
          placeholder="Ex: Client would like to upgrade to the Premium Care plan."
          onBlur={(e): void => {
            validateText(e.target.value);
          }}
          onChange={(e): void => {
            setSubmitText(e.target.value);
            validateText(e.target.value);
          }}
          rows={3}
        />
        <Text as="p" mt={4}>
          The text above plus any information entered in previous steps will be reviewed by a Care support
          representative who will contact{' '}
          {selectedEmail ? (
            <Text variant="info" fontWeight="semibold">
              {selectedEmail}
            </Text>
          ) : (
            'you'
          )}{' '}
          as soon as possible.
        </Text>
      </ModalBody>
      <ModalFooter justifyContent="space-between">
        <Button
          variant="tertiary"
          onClick={(): void => {
            setSubmitText('');
            handleBack();
          }}
          isDisabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          isDisabled={submitText.length === 0 || submitText.length > DESCRIPTION_MAX_CHAR_COUNT}
        >
          Submit Request
        </Button>
      </ModalFooter>
    </>
  );
};

export default GetHelpStep;
